import type {
  AdminConfig,
  AdminIdentity,
  AuthLoginResponse,
  AuthSession,
  ChangeEntry,
  Environment,
  EnvironmentCreate,
  EnvironmentEvaluateRequest,
  EvaluateResponse,
  EventsSummary,
  Flag,
  FlagCreate,
  FlagDefinitionDetail,
  FlagDefinitionPut,
  FlagUpdate,
  ImpressionsSummary,
  Segment,
  SegmentCreate,
  SegmentKeysPatch,
  SdkKey,
  SdkKeyCreate,
  SdkKeyCreated,
  Workspace,
  WorkspaceCreate,
  WorkspaceUpdate,
} from "@spoonity/flags-contract";
import { apiRequest, authRequest } from "./client";

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface PageParams {
  cursor?: string | null;
  limit?: number;
  [key: string]: string | number | boolean | null | undefined;
}

/* ------------------------------------------------------- config & identity */

export const getConfig = () => apiRequest<AdminConfig>("/config", { anonymous: true });
export const getMe = () => apiRequest<AdminIdentity>("/me");

/* -------------------------------------------- sign-in (CONTRACT §2.4) */

export const login = (email: string, password: string) =>
  authRequest<AuthLoginResponse>("/login", { method: "POST", body: { email, password }, anonymous: true });
export const selectVendor = (selectionToken: string, vendorId: string) =>
  authRequest<AuthSession>("/select-vendor", {
    method: "POST",
    body: { selection_token: selectionToken, vendor_id: vendorId },
    anonymous: true,
  });
export const switchVendor = (vendorId: string) =>
  authRequest<AuthSession>("/switch-vendor", { method: "POST", body: { vendor_id: vendorId } });
export const logout = () => authRequest<void>("/logout", { method: "POST", anonymous: true });

/* --------------------------------------------------------------- workspaces */

export const listWorkspaces = (params: PageParams = {}) =>
  apiRequest<Page<Workspace>>("/workspaces", { query: params });
export const createWorkspace = (body: WorkspaceCreate) =>
  apiRequest<Workspace>("/workspaces", { method: "POST", body });
export const getWorkspace = (ws: string) => apiRequest<Workspace>(`/workspaces/${ws}`);
export const updateWorkspace = (ws: string, body: WorkspaceUpdate) =>
  apiRequest<Workspace>(`/workspaces/${ws}`, { method: "PATCH", body });

/* ------------------------------------------------------------- environments */

export const listEnvironments = (ws: string, params: PageParams = {}) =>
  apiRequest<Page<Environment>>(`/workspaces/${ws}/environments`, { query: params });
export const createEnvironment = (ws: string, body: EnvironmentCreate) =>
  apiRequest<Environment>(`/workspaces/${ws}/environments`, { method: "POST", body });
export const getEnvironment = (env: string) => apiRequest<Environment>(`/environments/${env}`);

/* ---------------------------------------------------------------- SDK keys */

export const listKeys = (env: string, params: PageParams = {}) =>
  apiRequest<Page<SdkKey>>(`/environments/${env}/keys`, { query: params });
export const createKey = (env: string, body: SdkKeyCreate) =>
  apiRequest<SdkKeyCreated>(`/environments/${env}/keys`, { method: "POST", body });
export const revokeKey = (env: string, id: string) =>
  apiRequest<void>(`/environments/${env}/keys/${id}`, { method: "DELETE" });

/* -------------------------------------------------------------------- flags */

export interface FlagListParams extends PageParams {
  q?: string;
  tag?: string;
  archived?: boolean;
}

export const listFlags = (ws: string, params: FlagListParams = {}) =>
  apiRequest<Page<Flag>>(`/workspaces/${ws}/flags`, { query: params });
export const createFlag = (ws: string, body: FlagCreate) =>
  apiRequest<Flag>(`/workspaces/${ws}/flags`, { method: "POST", body });
export const getFlag = (ws: string, flag: string) =>
  apiRequest<Flag>(`/workspaces/${ws}/flags/${flag}`);
export const updateFlag = (ws: string, flag: string, body: FlagUpdate) =>
  apiRequest<Flag>(`/workspaces/${ws}/flags/${flag}`, { method: "PATCH", body });

/* ----------------------------------------------------------- definitions */

export const getDefinition = (env: string, flag: string) =>
  apiRequest<FlagDefinitionDetail>(`/environments/${env}/flags/${flag}/definition`);
export const putDefinition = (env: string, flag: string, body: FlagDefinitionPut) =>
  apiRequest<FlagDefinitionDetail>(`/environments/${env}/flags/${flag}/definition`, {
    method: "PUT",
    body,
  });
export const killFlag = (env: string, flag: string) =>
  apiRequest<FlagDefinitionDetail>(`/environments/${env}/flags/${flag}/kill`, { method: "POST" });
export const restoreFlag = (env: string, flag: string) =>
  apiRequest<FlagDefinitionDetail>(`/environments/${env}/flags/${flag}/restore`, {
    method: "POST",
  });
export const getDefinitionHistory = (env: string, flag: string, params: PageParams = {}) =>
  apiRequest<Page<ChangeEntry>>(`/environments/${env}/flags/${flag}/definition/history`, {
    query: params,
  });

/* ----------------------------------------------------------------- segments */

export const listSegments = (env: string, params: PageParams = {}) =>
  apiRequest<Page<Segment>>(`/environments/${env}/segments`, { query: params });
export const createSegment = (env: string, body: SegmentCreate) =>
  apiRequest<Segment>(`/environments/${env}/segments`, { method: "POST", body });
export const getSegment = (env: string, name: string) =>
  apiRequest<Segment>(`/environments/${env}/segments/${name}`);
export const deleteSegment = (env: string, name: string) =>
  apiRequest<void>(`/environments/${env}/segments/${name}`, { method: "DELETE" });
export const listSegmentKeys = (env: string, name: string, params: PageParams = {}) =>
  apiRequest<Page<string>>(`/environments/${env}/segments/${name}/keys`, { query: params });
export const patchSegmentKeys = (env: string, name: string, body: SegmentKeysPatch) =>
  apiRequest<Segment>(`/environments/${env}/segments/${name}/keys`, { method: "PATCH", body });
export const importSegmentCsv = (env: string, name: string, csv: string) =>
  apiRequest<Segment>(`/environments/${env}/segments/${name}/import`, {
    method: "POST",
    rawBody: { contentType: "text/csv", text: csv },
  });

/* -------------------------------------------------------------------- audit */

export interface ChangesParams extends PageParams {
  action?: string;
  target?: string;
  actor?: string;
  from?: string;
  to?: string;
}

export const listEnvironmentChanges = (env: string, params: ChangesParams = {}) =>
  apiRequest<Page<ChangeEntry>>(`/environments/${env}/changes`, { query: params });
export const listWorkspaceChanges = (ws: string, params: ChangesParams = {}) =>
  apiRequest<Page<ChangeEntry>>(`/workspaces/${ws}/changes`, { query: params });

/* ----------------------------------------------------------------- insights */

export interface SummaryParams {
  flag?: string;
  eventType?: string;
  from?: string;
  to?: string;
  [key: string]: string | number | boolean | null | undefined;
}

export const getImpressionsSummary = (env: string, params: SummaryParams) =>
  apiRequest<ImpressionsSummary>(`/environments/${env}/impressions/summary`, { query: params });
export const getEventsSummary = (env: string, params: SummaryParams) =>
  apiRequest<EventsSummary>(`/environments/${env}/events/summary`, { query: params });
export const evaluate = (env: string, body: EnvironmentEvaluateRequest) =>
  apiRequest<EvaluateResponse>(`/environments/${env}/evaluate`, { method: "POST", body });
